'use client';

import { useEffect, useRef, useState, memo } from 'react';

import { useMediaQuery } from '@mui/material';
import { Types, useAmplifyServiceContext } from '@scorenco/core';
import { v4 as uuidv4 } from 'uuid';

import { LeatherText } from './LeatherText';
import { trackAdClick } from './trackAdClick';

interface Ad {
  id: string;
  image_url: string;
  display_type: string;
  position: string;
  sport_id?: string;
  department_id?: string;
  impression_pixel_url?: string;
  link_url?: string;
}

interface AdsProps {
  type: string;
  sport_id?: Types.Clubs_Sport['id'];
  department_id?: Types.Places_Department['id'];
  style?: React.CSSProperties;
  club_name?: string;
  logged_in?: number;
  screen?: string;
}

declare global {
  interface Window {
    __isPremiumUser?: boolean;
  }
}

const isPremiumClient = (): boolean =>
  typeof window !== 'undefined' && window.__isPremiumUser === true;

const adsInstances = new Map<
  string,
  { uuid: string; ad: Ad | null; hasRebond?: boolean; shouldShow: boolean }
>();

const getLocationHeader = async (
  url: string
): Promise<{ hasRebond: boolean; isRugby: boolean }> => {
  const hasRebond = url.includes('rebond') && url.includes('vintage');
  const isRugby = url.includes('rugby');

  if (typeof window === 'undefined') {
    return { hasRebond, isRugby };
  }

  try {
    const locationUrl = new URL(url, window.location.href);

    if (locationUrl.origin !== window.location.origin) {
      return { hasRebond, isRugby };
    }

    const response = await fetch(url, { method: 'HEAD' });

    return {
      hasRebond:
        (response.url?.includes('rebond') &&
          response.url?.includes('vintage')) ||
        false,
      isRugby: response.url?.includes('rugby') || false,
    };
  } catch (_error) {
    return { hasRebond, isRugby };
  }
};

const AdsComponent = ({
  type,
  sport_id,
  department_id,
  style,
  club_name,
  logged_in,
  screen: screenProp,
}: AdsProps) => {
  const { user } = useAmplifyServiceContext();
  const [hasRebondState, setHasRebondState] = useState<boolean | null>(null);
  const [isRugbyState, setIsRugbyState] = useState<boolean | null>(null);
  const [ad, setAd] = useState<Ad | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const hasBeenHiddenRef = useRef(false);
  const [hasBeenHidden, setHasBeenHidden] = useState(false);

  const isMobile = useMediaQuery('(max-width:768px)');
  const screen = screenProp || (isMobile ? 'mobile' : 'desktop');

  const isLoggedIn =
    logged_in !== undefined ? logged_in : user?.info?.id ? 1 : 0;

  const isClubAdmin = (user?.info?.club_admin?.clubs?.length ?? 0) > 0 ? 1 : 0;

  const getAdDimensions = () => {
    if (isMobile) {
      return isLoggedIn ? '320x160' : '300x250';
    }
    return isLoggedIn ? '1000x250' : '1000x400';
  };

  const [width, height] = getAdDimensions().split('x').map(Number);

  const instanceKey = `${type}-${sport_id || 'no-sport'}${department_id || ''}`;

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        hasBeenHiddenRef.current = true;
        setHasBeenHidden(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const instance = adsInstances.get(instanceKey) ?? {
      uuid: uuidv4(),
      ad: null,
      hasRebond: false,
      shouldShow: true,
    };
    adsInstances.set(instanceKey, instance);

    const fetchAd = async () => {
      try {
        setIsLoading(true);
        setError(false);

        const params = new URLSearchParams({
          type: type,
          logged_in: isLoggedIn.toString(),
          club_admin: isClubAdmin.toString(),
          screen,
          position: '1',
        });

        // Ajouter sport seulement s'il existe
        if (sport_id) {
          params.append('sport', sport_id.toString());
        }

        // Ajouter department seulement s'il existe
        if (department_id) {
          params.append('department', department_id.toString());
        }

        const baseImageUrl = `https://${process.env.NEXT_PUBLIC_REST_ENDPOINT}/ads/image/?${params.toString()}`;
        const response = await fetch(`${baseImageUrl}&format=json`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const imageUrl = `${baseImageUrl}&id=${instance.uuid}&format=image`;
        let data;
        try {
          data = await response.json();
        } catch (_error) {
          // Si la réponse n'est pas du JSON, utiliser le fallback
          console.warn('Réponse non-JSON reçue, utilisation du fallback');
          instance.ad = {
            id: instance.uuid,
            image_url: imageUrl,
            display_type: type,
            position: '1',
          };
          setAd(instance.ad);
          adsInstances.set(instanceKey, instance);
          return;
        }

        // Gérer différents formats de réponse possibles
        let adData = null;
        if (data.ad) {
          adData = data.ad;
        } else if (data.image_url) {
          // Si l'API retourne directement les données d'annonce
          adData = data;
        } else if (data.image) {
          // Format alternatif possible
          adData = {
            id: data.id || instance.uuid,
            image_url: data.image,
            display_type: data.display_type || type,
            position: data.position || '1',
            sport_id: data.sport_id,
            department_id: data.department_id,
            impression_pixel_url: data.impression_pixel_url,
            link_url: data.link_url,
          };
        }

        // Gérer le cas où image_url est un objet (structure imbriquée)
        if (
          adData &&
          typeof adData.image_url === 'object' &&
          adData.image_url !== null
        ) {
          // Extraire les données de l'objet imbriqué
          const nestedData = adData.image_url;
          adData = {
            id: nestedData.id || adData.id || instance.uuid,
            image_url: nestedData.image_url,
            display_type:
              nestedData.display_type || adData.display_type || type,
            position: nestedData.position || adData.position || '1',
            sport_id: nestedData.sport_id,
            department_id: nestedData.department_id,
            impression_pixel_url: nestedData.impression_pixel_url,
            link_url: nestedData.link_url,
          };
        }

        if (adData) {
          instance.ad = adData;
          setAd(adData);
          adsInstances.set(instanceKey, instance);
        } else {
          // Fallback vers l'ancienne méthode si pas de données JSON
          console.warn('Aucune donnée JSON reçue, utilisation du fallback');
          instance.ad = {
            id: instance.uuid,
            image_url: imageUrl,
            display_type: type,
            position: '1',
          };
          setAd(instance.ad);
          adsInstances.set(instanceKey, instance);
        }
      } catch (err) {
        console.error('Erreur lors de la récupération de la publicité:', err);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    // Vérifier si on a déjà une annonce pour cette instance
    void (async () => {
      if (instance.ad) {
        setAd(instance.ad);
        setIsLoading(false);
      } else if (instance.shouldShow && !hasBeenHiddenRef.current) {
        await fetchAd();
      }
    })();
  }, [instanceKey, type, sport_id, department_id, isLoggedIn, screen]);

  // Gérer l'impression pixel si elle existe
  useEffect(() => {
    if (ad?.impression_pixel_url) {
      const img = new Image();
      img.src = ad.impression_pixel_url;
      img.style.display = 'none';
      document.body.appendChild(img);

      return () => {
        if (document.body.contains(img)) {
          document.body.removeChild(img);
        }
      };
    }
  }, [ad?.impression_pixel_url]);

  useEffect(() => {
    if (!ad?.image_url) return;
    const currentInstance = adsInstances.get(instanceKey);
    if (!currentInstance || currentInstance.hasRebond !== false) return;

    getLocationHeader(ad.image_url).then(({ hasRebond, isRugby }) => {
      const inst = adsInstances.get(instanceKey);
      if (inst) {
        adsInstances.set(instanceKey, { ...inst, hasRebond });
      }
      setHasRebondState(hasRebond);
      setIsRugbyState(isRugby);
    });
  }, [ad, instanceKey]);

  if (error || hasBeenHidden || isLoading || !ad) {
    return null;
  }

  // Utiliser link_url si disponible, sinon utiliser le lien par défaut
  const link =
    ad.link_url ||
    `https://${process.env.NEXT_PUBLIC_REST_ENDPOINT}/ads/campaign/${ad.id}/link/`;

  const handleClick = () => {
    // Si on a un link_url personnalisé, faire le tracking sans suivre
    // la redirection vers le domaine annonceur (évite une erreur CORS).
    if (ad.link_url) {
      trackAdClick(
        `https://${process.env.NEXT_PUBLIC_REST_ENDPOINT}/ads/campaign/${ad.id}/link/`
      );
    }
  };

  return (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        position: 'relative',
        display: 'block',
        width: '100%',
        textDecoration: 'none',
        color: 'inherit',
        cursor: 'pointer',
        WebkitTapHighlightColor: 'transparent',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
      }}
      onClick={handleClick}
    >
      <div
        style={{
          position: 'relative',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <img
          src={ad.image_url}
          alt="ads"
          width={width}
          height={height}
          style={{ width: '100%', height: 'auto', ...style }}
          onError={() => setError(true)}
        />
        {hasRebondState && (
          <LeatherText isRugby={isRugbyState || false}>{club_name}</LeatherText>
        )}
      </div>
    </a>
  );
};

const AdsWithoutPremium = (props: AdsProps) => {
  if (isPremiumClient()) {
    return null;
  }
  return <AdsComponent {...props} />;
};

export const Ads = memo(AdsWithoutPremium);
